import { useEffect, useState, type FunctionComponent } from "react";
import {
  RiFileTextLine,
  RiDeleteBinLine,
  RiPencilLine,
  RiEyeLine,
  RiAddLine,
  RiFileList3Line,
  RiCalendarLine,
  RiFileLine
} from "@remixicon/react";
import { IconButton, Tooltip, Typography, Paper, Button, Backdrop, CircularProgress, Skeleton, Box, useTheme, useMediaQuery, Chip } from "@mui/material";
import { isAfter, subDays, startOfDay } from "date-fns";

import { obtenerDescripcionTipoDocumento, type PersonalDocumentUser } from "@/shared/types/interfaces/DocumentoPersonalUsuario.interface";
import { TipoDocumentoPersonalEnum } from "@/shared/types/enums/TipoDocumentoPersonal.enum";
import CargarDocumentoPersonalModal, { getDocumentIcon } from "./CargarDocumentoPersonalModal";
import FileViewer from "@/shared/components/FileViewer/FileViewer";
import FileIconHover from "@/shared/components/FileIconHover/FileIconHover";
import customAlert from "../../CustomAlert";
import { useObtenerDocumentosPersonalesUsuario, useDeletePersonalFile } from "@/shared/hooks/useDocumentosPersonales.hook";
import { useUserData } from "@/shared/hooks/useUserData.hook";
import { useTranslation } from 'react-i18next';

const DocumentosPersonales: FunctionComponent = () => {
    const { t } = useTranslation('documentosPersonales');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState<PersonalDocumentUser | undefined>(undefined);
    const [viewingFile, setViewingFile] = useState<PersonalDocumentUser | null>(null);
    const {fullName, identificacion, systemTime} = useUserData();

    const { data: documents = [], isLoading: isLoadingDocs } = useObtenerDocumentosPersonalesUsuario();
    const deleteMutation = useDeletePersonalFile();

    const isDeleting = deleteMutation.isPending;

    /** 
    * verificar si un documento es reciente (en función de la cantidad de días especificada). 
    * Predeterminado 7 días. Se compara la fecha de subida del documento con la fecha actual del sistema restando los días indicados. 
    */
    const isRecentDocument = (dateString: string, days: number = 7): boolean => {
        const docDate = new Date(dateString);
        const now = new Date(systemTime?.fechaActual as Date);
        const daysAgoStart = startOfDay(subDays(now, days));
        return isAfter(docDate, daysAgoStart);
    };

    /** Ordenar documentos por fecha de subida (más recientes primero) */
    const sortedDocuments = [...documents].sort((a, b) =>
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
    );

    /** Formatear la fecha */
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleOpenModal = () => {
        setSelectedDoc(undefined);
        setIsModalOpen(true);
    };

    const handleEdit = (doc: PersonalDocumentUser) => {
        setSelectedDoc(doc);
        setIsModalOpen(true);
    };

    const handleDelete = (idPersonalDocumentUser: number, name: string) => {
        
        customAlert({
            icon: 'question',
            titulo: `<div class="text-2xl font-semibold">${t('confirmDelete.title')}</div>`,
            mensaje: t('confirmDelete.message', { name }),
            textoBotonAceptar: t('confirmDelete.confirmButton'),
            colorBotonAceptar: '#d32f2f',
            colorBotonCancelar: '#1976d2',
            textoBotonCancelar: t('confirmDelete.cancelButton'),
            botonCancelar: true,
            cerrarEsc: false,
            cerrarClicFuera: false,
            callback(isConfirmed) {
                if (isConfirmed) {
                    deleteMutation.mutate(idPersonalDocumentUser);
                }
            },
        });
                
        // if (confirm(`¿Estás seguro de que deseas eliminar el documento "${name}"?`)) {
        //     DocumentosPersonalesService.deletePersonalFile(id);
        //     loadDocuments();
        // }
    };

    const handleSave = () => {
        setIsModalOpen(false);
    };

    const handleView = (doc: PersonalDocumentUser) => {
        setViewingFile(doc);
    };

    return (
        <div className="flex flex-col gap-6 pb-2 px-2 h-full">
            <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center gap-4">
                <div className="text-center sm:text-left">
                    <Typography variant="h6" fontWeight="600" color="text.primary">
                        {t('title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('description')}
                    </Typography>
                </div>
                <Button
                    variant="contained"
                    startIcon={<RiAddLine />}
                    onClick={handleOpenModal}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                    {t('addDocument')}
                </Button>
            </div>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                {isMobile ? (
                    
                    <div className="divide-y divide-gray-200">
                        {isLoadingDocs ? (
                            Array.from({ length: 3 }).map((_, index) => (
                                <Box key={index} sx={{ p: 3 }}>
                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <Skeleton variant="circular" width={40} height={40} />
                                        <Box>
                                            <Skeleton variant="text" width={150} height={20} />
                                            <Skeleton variant="text" width={100} height={16} />
                                        </Box>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Skeleton variant="text" width={80} height={20} />
                                            <Skeleton variant="text" width={60} height={16} />
                                        </Box>
                                        <Box display="flex" gap={1}>
                                            <Skeleton variant="circular" width={32} height={32} />
                                            <Skeleton variant="circular" width={32} height={32} />
                                            <Skeleton variant="circular" width={32} height={32} />
                                        </Box>
                                    </Box>
                                </Box>
                            ))
                        ) : sortedDocuments.length === 0 ? (
                            <Box sx={{ p: 6, textAlign: 'center' }}>
                                <RiFileList3Line size={48} className="text-gray-300 mx-auto mb-4" />
                                <Typography variant="body1" gutterBottom>
                                    {t('empty.title')}
                                </Typography>
                                <Button
                                    variant="text"
                                    startIcon={<RiAddLine />}
                                    onClick={handleOpenModal}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {t('empty.button')}
                                </Button>
                            </Box>
                        ) : (
                            sortedDocuments.map((doc) => (
                                <Box key={doc.idPersonalDocumentUser} sx={{ p: 3, '&:hover': { bgcolor: 'grey.50' } }}>
                                    {/* Header con ícono y tipo de documento */}
                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <Box sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: 'primary.light',
                                            color: 'primary.contrastText',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}>
                                            {getDocumentIcon(doc.idType as TipoDocumentoPersonalEnum, { size: 20 })}
                                            {/* {isRecentDocument(doc.uploadedAt) && (
                                                <Box sx={{
                                                    position: 'absolute',
                                                    top: -4,
                                                    right: -4,
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    bgcolor: 'success.main',
                                                    border: '2px solid white'
                                                }} />
                                            )} */}
                                        </Box>
                                        <Box flex={1}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {/* <Typography variant="body1" fontWeight="600" color="text.primary" className="truncate">
                                                    {obtenerDescripcionTipoDocumento(doc.idType as TipoDocumentoPersonalEnum)}
                                                </Typography> */}
                                                {isRecentDocument(doc.uploadedAt) && (
                                                    <Chip
                                                        label={t('new')}
                                                        size="small"
                                                        color="success"
                                                        sx={{ fontSize: '0.65rem', height: '18px', '& .MuiChip-label': { px: 0.5 } }}
                                                    />
                                                )}
                                            </Box>
                                            <Chip
                                                label={`ID: ${doc.idPersonalDocumentUser}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ mt: 0.5, fontSize: '0.7rem', height: '20px' }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Información del documento */}
                                    <Box mb={2}>
                                        <Typography variant="body2" color="text.primary" fontWeight="500" className="truncate">
                                            {doc.name}
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                                            {/* <RiFileLine size={14} color={theme.palette.text.secondary} /> */}
                                            {/* <Typography variant="caption" color="text.secondary">
                                                {formatFileSize(doc.fileSize)}
                                            </Typography> */}
                                            <Box sx={{ mx: 1, width: '4px', height: '4px', borderRadius: '50%', bgcolor: 'text.secondary' }} />
                                            <RiCalendarLine size={14} color={theme.palette.text.secondary} />
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(doc.uploadedAt)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Usuario propietario */}
                                    <Box mb={2} sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Propietario del documento
                                        </Typography>
                                        <Typography variant="body2" fontWeight="500" color="text.primary">
                                            {fullName?.toUpperCase()}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ID: {identificacion}
                                        </Typography>
                                    </Box>

                                    {/* Acciones */}
                                    <Box 
                                        display="flex" 
                                        justifyContent="center" 
                                        gap={4} 
                                        sx={{ 
                                            border: '1px solid', 
                                            borderColor: 'divider', 
                                            borderRadius: 1, 
                                            px: 2, 
                                            py: 1 
                                        }}
                                    >
                                        <FileIconHover
                                            fileName={doc.fileName}
                                            onView={() => handleView(doc)}
                                            size="xs"
                                        />
                                        <Tooltip title={t('edit')} arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEdit(doc)}
                                                sx={{
                                                    color: 'text.secondary',
                                                    '&:hover': { bgcolor: 'action.hover' }
                                                }}
                                            >
                                                <RiPencilLine size={18} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('delete')} arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(doc.idPersonalDocumentUser, doc.name)}
                                                sx={{
                                                    color: 'error.main',
                                                    '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' }
                                                }}
                                            >
                                                <RiDeleteBinLine size={18} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            ))
                        )}
                    </div>
                ) : (
                    // Escritorio
                    <div className="overflow-auto" style={{ maxHeight: '350px' }}>
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t('table.document')}
                                    </th>
                                    {/* <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Tipo
                                    </th> */}
                                    {/* <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Tamaño
                                    </th> */}
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t('table.uploadDate')}
                                    </th>
                                    <th scope="col" className="relative px-6 py-3">
                                        <span className="sr-only">{t('table.actions')}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoadingDocs ? (
                                    Array.from({ length: 3 }).map((_, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <Skeleton variant="circular" width={36} height={36} className="mr-3" />
                                                    <div>
                                                        <Skeleton variant="text" width={150} height={20} />
                                                        <Skeleton variant="text" width={80} height={16} />
                                                    </div>
                                                </div>
                                            </td>
                                            {/* <td className="px-6 py-4 whitespace-nowrap">
                                                <Skeleton variant="rectangular" width={120} height={24} sx={{ borderRadius: 1 }} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Skeleton variant="text" width={60} height={20} />
                                            </td> */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Skeleton variant="text" width={100} height={20} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <Box display="flex" gap={1} justifyContent="flex-end">
                                                    <Skeleton variant="circular" width={32} height={32} />
                                                    <Skeleton variant="circular" width={32} height={32} />
                                                    <Skeleton variant="circular" width={32} height={32} />
                                                </Box>
                                            </td>
                                        </tr>
                                    ))
                                ) : sortedDocuments.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                                            <div className="flex flex-col items-center gap-2">
                                                <RiFileList3Line size={48} className="text-gray-300" />
                                                <Typography variant="body1">
                                                    {t('empty.title')}
                                                </Typography>
                                                <Button
                                                    variant="text"
                                                    startIcon={<RiAddLine />}
                                                    onClick={handleOpenModal}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {t('empty.button')}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    sortedDocuments.map((doc) => (
                                        <tr key={doc.idPersonalDocumentUser} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="p-2 bg-blue-50 text-blue-600 rounded-lg mr-3 relative">
                                                        {getDocumentIcon(doc.idType as TipoDocumentoPersonalEnum, { size: 20 })}
                                                        {/* {isRecentDocument(doc.uploadedAt) && (
                                                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                                                        )} */}
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="text-sm font-medium text-gray-900 max-w-[200px] truncate">
                                                                {doc.name}
                                                            </div>
                                                            {isRecentDocument(doc.uploadedAt) && (
                                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                    ({t('new')})
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500 max-w-[200px] truncate">
                                                            ID: {doc.idPersonalDocumentUser}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            {/* <td className="px-6 py-4 whitespace-nowrap">
                                                <Chip
                                                    label={obtenerDescripcionTipoDocumento(doc.idType as TipoDocumentoPersonalEnum)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        height: '24px',
                                                        '& .MuiChip-label': { px: 1 }
                                                    }}
                                                />
                                            </td> */}
                                            {/* <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm text-gray-900">
                                                    <RiFileLine size={14} className="mr-1 text-gray-400" />
                                                    {formatFileSize(doc.fileSize)}
                                                </div>
                                            </td> */}
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm text-gray-900">
                                                    <RiCalendarLine size={14} className="mr-1 text-gray-400" />
                                                    {formatDate(doc.uploadedAt)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end gap-2">
                                                    <FileIconHover
                                                        fileName={doc.fileName}
                                                        onView={() => handleView(doc)}
                                                        size="xs"
                                                    />
                                                    <Tooltip title={t('edit')} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEdit(doc)}
                                                            className="text-gray-600 hover:bg-gray-50"
                                                        >
                                                            <RiPencilLine size={18} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('delete')} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDelete(doc.idPersonalDocumentUser, doc.name)}
                                                            className="text-red-600 hover:bg-red-50"
                                                        >
                                                            <RiDeleteBinLine size={18} className="text-red-600" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </Paper>

            <CargarDocumentoPersonalModal
                isOpen={isModalOpen}
                closeModal={() => setIsModalOpen(false)}
                onSave={handleSave}
                initialData={selectedDoc}
            />

            {viewingFile && (
                <FileViewer
                    fileUrl={viewingFile.fileUrl}
                    fileName={viewingFile.fileName}
                    autoOpen={true}
                    onClose={() => setViewingFile(null)}
                />
            )}

            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isDeleting}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </div>
    );
};

export default DocumentosPersonales;
